import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Search, Package, MoreVertical } from 'lucide-react';
import BottomNavigation from '../../components/common/BottomNavigation';
import { useApp } from '../../context/AppContext';
import { useData } from '../../context/DataContext';
import { Product } from '../../types';

const AdminProductList: React.FC = () => {
  const { setCurrentScreen } = useApp();
  const { products } = useData();
  const [searchTerm, setSearchTerm] = useState('');

  // Filter products
  const filteredProducts = useMemo(() => {
    return products.filter(product =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.brand.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [products, searchTerm]);

  const handleBack = () => {
    setCurrentScreen('admin-dashboard');
  };

  const handleManageProducts = () => {
    setCurrentScreen('admin-product');
  };


  const ProductListItem: React.FC<{ product: Product; index: number }> = ({ product, index }) => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      className="flex items-center justify-between p-4 bg-slate-800 rounded-lg border border-slate-700 hover:bg-slate-750 transition-colors duration-200"
    >
      <div className="flex items-center space-x-4">
        <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
          {product.image ? (
            <img 
              src={product.image} 
              alt={product.name}
              className="w-10 h-10 rounded-lg object-cover"
            />
          ) : (
            <Package size={20} className="text-slate-400" />
          )}
        </div>
        <div>
          <h3 className="text-white font-medium">{product.name}</h3>
          <p className="text-slate-400 text-sm">{product.brand} • {product.category}</p>
        </div>
      </div>
      <div className="flex items-center space-x-6">
        <div className="text-right">
          <p className="text-white font-semibold">₹{product.price}</p>
          <p className="text-slate-400 text-sm">{product.stock} in stock</p>
        </div>
        <button className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors duration-200">
          <MoreVertical size={16} />
        </button>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between p-6 bg-slate-800 border-b border-slate-700">
        <div className="flex items-center space-x-4">
          <motion.button
            onClick={handleBack}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors duration-200"
          >
            <ArrowLeft size={20} />
          </motion.button>
          <div>
            <h1 className="text-xl font-bold text-white">Products</h1>
            <p className="text-slate-400 text-sm">{products.length} total products</p>
          </div>
        </div>
        <motion.button
          onClick={handleManageProducts}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex items-center space-x-2 bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
        >
          <Package size={16} />
          <span>Manage</span>
        </motion.button>
      </div>

      <div className="p-6 space-y-6">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
          <input
            type="text"
            placeholder="Search products by name, brand, category, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>

        {/* Products List */}
        <div className="space-y-3">
          {filteredProducts.length > 0 ? (
            filteredProducts.map((product, index) => (
              <ProductListItem key={product.id} product={product} index={index} />
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <div className="w-24 h-24 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package size={32} className="text-slate-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                {searchTerm ? 'No products found' : 'No products yet'}
              </h3>
              <p className="text-slate-400 mb-6">
                {searchTerm 
                  ? 'Try adjusting your search terms' 
                  : 'Products will appear here when added'
                }
              </p>
              {!searchTerm && (
                <motion.button
                  onClick={handleManageProducts}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-lg transition-colors duration-200"
                >
                  Add First Product
                </motion.button>
              )}
            </motion.div>
          )}
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default AdminProductList;
